import axios from 'axios';
import { notifyUnauthorized } from './authEvents';

// Sign-in, users and the activity log. Sign-in calls handle their own 401s; anywhere else a 401
// means the session ended, so the app goes back to the sign-in page.
const SIGN_IN_PATHS = ['/api/v2/auth/'];

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !SIGN_IN_PATHS.some(path => url.startsWith(path))) {
      notifyUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Error carrying the API's `detail` message when there is one
const toError = (error, fallbackMessage) => {
  const detail = error.response?.data?.detail;
  const apiError = new Error(typeof detail === 'string' ? detail : fallbackMessage);
  apiError.status = error.response?.status;
  return apiError;
};

const call = async (request, fallbackMessage) => {
  try {
    const { data } = await request();
    return data;
  } catch (error) {
    throw toError(error, fallbackMessage);
  }
};

const accessApi = {
  /** What the sign-in page needs: { google_client_id, dev_login } */
  config: () => call(() => apiClient.get('/api/v2/auth/config'), 'Could not reach the server'),

  /** The release currently running: { status, service, version } */
  health: () => call(() => apiClient.get('/api/v2/health'), 'Could not reach the server'),

  /** Who is signed in: { id, email, name, picture_url, roles }; rejects with status 401 when nobody is */
  me: () => call(() => apiClient.get('/api/v2/auth/me'), 'Not signed in'),

  /** Sign in with the ID token Google Identity Services returned */
  googleSignIn: (credential) => call(() => apiClient.post('/api/v2/auth/google', { credential }), 'Sign-in failed'),

  /** Development sign-in as an existing user (only when the server allows it) */
  devLogin: (email) => call(() => apiClient.post('/api/v2/auth/dev-login', { email }), 'Sign-in failed'),

  signOut: () => call(() => apiClient.post('/api/v2/auth/logout'), 'Sign-out failed'),

  /** Everyone who can sign in or asked to: { users, roles, email_enabled } (admins only) */
  listUsers: () => call(() => apiClient.get('/api/v2/users'), 'Failed to load users'),

  /** Let someone sign in: { email, name, roles, send_welcome } */
  addUser: (fields) => call(() => apiClient.post('/api/v2/users', fields), 'Failed to add the user'),

  /** Send someone the welcome email, again if need be */
  sendWelcome: (userId) => call(() => apiClient.post(`/api/v2/users/${userId}/welcome`), "Couldn't send the email"),

  /** Change name, roles, or `active` (false turns their access off and signs them out) */
  updateUser: (userId, changes) => call(() => apiClient.patch(`/api/v2/users/${userId}`, changes), 'Failed to update the user'),

  /**
   * Recent changes, newest first
   * @param {Object} params - action (prefix such as 'contact.'), subject_type + subject_id, before_id, limit
   * @returns {Promise<{events: Array, next_before_id: ?number}>}
   */
  activity: (params = {}) => call(() => apiClient.get('/api/v2/audit', { params }), 'Failed to load the activity'),
};

export default accessApi;
